/**
 * Whether the weather allows the work — and whether we actually know.
 *
 * A window clear of traffic is worthless if the sealant will not cure in it,
 * or if the crew is not legally permitted outside. This is the other half of
 * that answer, and three things separate it from a weather widget:
 *
 * IT NEVER MERGES A FORECAST INTO AN OBSERVATION. Every answer says which it
 * used and, for a forecast, how far ahead it was issued. A plan made on a
 * forecast is a perfectly reasonable plan; a plan made on a forecast recorded
 * as a measurement is one nobody can audit after it goes wrong.
 *
 * IT REFUSES TO EVALUATE A SURFACE LIMIT FROM AIR TEMPERATURE. Asphalt in
 * afternoon sun sits twenty to thirty degrees above the air, so a sealant with
 * a 10–50 °C substrate window can fail at either end on a day the air
 * temperature calls perfect. Where the limit says `surface` and only air is on
 * file, the answer is UNKNOWN with the missing reading named.
 *
 * AND IT DISTINGUISHES WHY. A material limit ruins the work and can be argued
 * about; a personnel limit hurts somebody; a regulatory limit — a midday
 * summer work ban — is not negotiable however willing the crew is.
 *
 * THE TRI-STATE. `allowed` is true, false or NULL, and null is the important
 * one: it means the limits could not be evaluated. Absence of a breach is not
 * permission, and the platform says so in its own words.
 */

export type LimitKind = "material" | "personnel" | "regulatory" | string;

export interface Breach {
  kind: LimitKind;
  why: string;
}

export interface Unknown {
  kind: LimitKind;
  why: string;
}

export interface Conditions {
  known: boolean;
  /** Which record this came from — observation or forecast, never merged. */
  basis?: string;
  whyNot?: string;
  airTempC?: number;
  surfaceTempC?: number;
  windKt?: number;
  gustKt?: number;
  visibilityM?: number;
  humidity?: number;
}

export interface CanWork {
  category: string;
  at: string;
  conditions: Conditions;
  breaches: Breach[];
  /** Limits that could not be evaluated. Not passes. */
  unknowns: Unknown[];
  limitsOnFile: number;
  checksMade: number;
  known: boolean;
  /** true, false, or null — and null is not a soft yes. */
  allowed: boolean | null;
  verdict: string;
}

export type WorkVerdict = "allowed" | "blocked" | "unknown";

/**
 * The tri-state, read in the safe order.
 *
 * `allowed === true` is required for a yes. Reading `!breaches.length` as
 * permission is exactly the bug the module was written against: a frame with
 * no breaches because nothing could be measured looks identical to one with no
 * breaches because everything passed.
 */
export function workVerdict(c: CanWork): WorkVerdict {
  if (c.allowed === true) return "allowed";
  if (c.allowed === false) return "blocked";
  return "unknown";
}

export const WORK_LABEL: Record<WorkVerdict, string> = {
  allowed: "Within limits",
  blocked: "Outside limits",
  unknown: "Cannot be determined",
};

/**
 * Whether a limit is negotiable at all.
 *
 * A regulatory ban is a clock rule, not a temperature one — a cool day does
 * not lift it, and no amount of crew willingness does either. Surfacing that
 * distinction stops a supervisor arguing with the wrong constraint.
 */
export function negotiable(kind: LimitKind): boolean {
  return kind !== "regulatory";
}

export const KIND_LABEL: Record<string, string> = {
  material: "Material — the work would be ruined",
  personnel: "Personnel — the crew would be harmed",
  regulatory: "Regulatory — not negotiable",
};
